import { useMemo } from "react";
import { useIntl } from "react-intl";
import { CourseMaterial } from "../index/coursesLoader";
import { useLayoutData } from "../layout/myCoursesLayoutLoader";
import { Offer } from "common/api/decimal/methods/compareAndPurchase";

const TAG_MAP = {
  eBook: ["etextbook", "e-book", "ebook"],
  accessCode: ["courseware", "access_code"],
  bundle: ["bundle"],
  print: ["print", "new", "used", "rental", "new_rental", "used_rental"],
  library: ["library"],
  oer: ["oer"],
  webAsset: ["web_asset"],
};

const ALWAYS_ACCESS_CODE_LINKS = ["launch_reveal_code"];
const ALWAYS_BUNDLE_LINKS = [
  "launch_bundle_reveal_code",
  "launch_bundle_courseware",
];
const ALWAYS_DISALLOWED_LINKS = ["launch_access_code"];
// when buttonLinks are not undefined, we require links to show the following formats
const FORMATS_REQUIRING_LINKS = TAG_MAP.accessCode;

export default function useMaterialTags({
  format,
  buttonLinks,
}: {
  format: CourseMaterial["materialFormat"] | Offer["variant"];
  buttonLinks?: CourseMaterial["buttonLinks"];
}) {
  const intl = useIntl();
  const { config } = useLayoutData();
  const tags: string[] = [];

  const addAccessCodeTag = () =>
    tags.push(intl.formatMessage({ defaultMessage: "Access Code" }));
  const addBundleTag = () =>
    tags.push(intl.formatMessage({ defaultMessage: "Bundle" }));

  const getBundleLinks = useMemo(() => {
    const bundleLinks = [...ALWAYS_BUNDLE_LINKS];
    if (format && TAG_MAP.bundle.includes(format)) {
      bundleLinks.push("lms_link");
    }
    return bundleLinks;
  }, [format]);

  const getDisallowedLinks = useMemo(() => {
    const disallowedLinks = [...ALWAYS_DISALLOWED_LINKS];
    if (format && !TAG_MAP.bundle.includes(format)) {
      disallowedLinks.push("lms_link");
    }
    return disallowedLinks;
  }, [format]);

  const hasAccessCodeLink = useMemo(
    () =>
      buttonLinks?.some(({ type }) =>
        ALWAYS_ACCESS_CODE_LINKS.includes(type)
      ) ?? false,
    [buttonLinks]
  );
  const hasBundleLink = useMemo(
    () =>
      buttonLinks?.some(({ type }) => getBundleLinks.includes(type)) ?? false,
    [buttonLinks, getBundleLinks]
  );
  const hasDisallowedLink = useMemo(
    () =>
      buttonLinks?.some(({ type }) => getDisallowedLinks.includes(type)) ??
      false,
    [buttonLinks, getDisallowedLinks]
  );
  const requiresLinks = useMemo(
    () =>
      (buttonLinks?.length === 0 &&
        format &&
        FORMATS_REQUIRING_LINKS.includes(format)) ??
      false,
    [buttonLinks, format]
  );

  if (
    hasAccessCodeLink ||
    hasBundleLink ||
    hasDisallowedLink ||
    requiresLinks ||
    !format
  ) {
    hasAccessCodeLink && addAccessCodeTag();
    hasBundleLink && addBundleTag();
    return tags;
  }

  if (TAG_MAP.eBook.includes(format)) {
    tags.push(intl.formatMessage({ defaultMessage: "eBook" }));
  }

  if (TAG_MAP.accessCode.includes(format)) {
    addAccessCodeTag();
  }

  if (TAG_MAP.bundle.includes(format)) {
    addBundleTag();
  }

  if (TAG_MAP.print.includes(format)) {
    tags.push(intl.formatMessage({ defaultMessage: "Print" }));
  }

  if (TAG_MAP.library.includes(format) && config.assets.labels.library) {
    tags.push(config.assets.labels.library);
  }

  if (TAG_MAP.oer.includes(format) && config.assets.labels.oer) {
    tags.push(config.assets.labels.oer);
  }

  if (TAG_MAP.webAsset.includes(format) && config.assets.labels.web) {
    tags.push(config.assets.labels.web);
  }

  return tags;
}
